import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Label } from "@/components/ui/label";
import { Cog } from "lucide-react";
import VariableList from "@/components/InputVariableConfigurator/VariableList";
import { useMemo, useRef, useState } from "react";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { useCustomParametersStore } from "../custom-parameters-store";
import { observer } from "mobx-react-lite";
import useAutoScroll from "@/components/InputVariableConfigurator/hooks/useAutoScroll";
import { getVariableValue } from "./utils";

export const HuggingFaceSettings = observer(() => {
  const store = useCustomParametersStore();
  const [isOpen, setIsOpen] = useState(false);
  const [accordion, setAccordion] = useState<"cookies" | "headers">("cookies");
  const { cookies, headers, timeout, isViewing } = store;

  const cookiesRef = useRef<HTMLDivElement>(null);
  const headersRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const itemsToScroll = accordion === "cookies" ? cookies : headers;
    return itemsToScroll.map((item) => ({
      id: String(item.id),
      key: item.key,
      value: item.value,
    })) as Variable[];
  }, [accordion, cookies, headers]);

  const { lastItemRef } = useAutoScroll(
    items,
    accordion === "cookies" ? cookiesRef : headersRef,
  );

  const updateHeaders = (variable: Variable) => {
    const stringValue = getVariableValue(variable);

    store.updateHeader({
      id: Number(variable.id),
      value: stringValue,
      key: variable.key,
    });
  };

  const updateCookies = (variable: Variable) => {
    const stringValue = getVariableValue(variable);

    store.updateCookie({
      id: Number(variable.id),
      value: stringValue,
      key: variable.key,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <BasicTooltip content="Set cookies, header, and timeout">
          <Button type="button" variant="ghost">
            <Cog className="mr-2 h-4 w-4" /> Optional Settings
          </Button>
        </BasicTooltip>
      </DialogTrigger>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>Optional Settings</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Set the optional settings for the HuggingFace model. All settings are
          being saved automatically.
        </DialogDescription>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <Label>Timeout</Label>
            <Input
              placeholder="Timeout"
              readOnly={isViewing}
              value={timeout}
              onChange={(e) => store.setTimeout(Number(e.target.value))}
            />
          </div>
          <Accordion
            type="single"
            onValueChange={(value) =>
              setAccordion(value as "cookies" | "headers")
            }
          >
            <AccordionItem value="cookies">
              <AccordionTrigger>Cookies</AccordionTrigger>
              <AccordionContent ref={cookiesRef}>
                <div className="max-h-[300px] overflow-y-auto">
                  <VariableList
                    readOnly={isViewing}
                    variables={
                      cookies.map((cookie) => ({
                        id: String(cookie.id),
                        key: cookie.key,
                        value: cookie.value,
                      })) as Variable[]
                    }
                    updateInputVariable={updateCookies}
                    removeInputVariable={(index: number) => {
                      if (cookies[index]?.id !== undefined) {
                        store.removeCookie(cookies[index]?.id);
                      }
                    }}
                    lastItemRef={lastItemRef}
                  />
                </div>
                <Button
                  className="mt-2"
                  type="button"
                  disabled={isViewing}
                  variant="outline"
                  onClick={() => {
                    store.addCookie({
                      id: cookies.length,
                      value: "",
                      key: "",
                    });
                  }}
                >
                  Add Cookie
                </Button>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="headers">
              <AccordionTrigger>Headers</AccordionTrigger>
              <AccordionContent ref={headersRef}>
                <div className="max-h-[300px] overflow-y-auto">
                  <VariableList
                    readOnly={isViewing}
                    variables={
                      headers.map((header) => ({
                        id: String(header.id),
                        key: header.key,
                        value: header.value,
                      })) as Variable[]
                    }
                    placeholder="Authorization: Bearer <token>"
                    tooltip="Header description"
                    updateInputVariable={updateHeaders}
                    removeInputVariable={(index: number) => {
                      if (headers[index]?.id !== undefined) {
                        store.removeHeader(headers[index]?.id);
                      }
                    }}
                    lastItemRef={headersRef}
                  />
                </div>
                <Button
                  className="mt-2"
                  type="button"
                  disabled={isViewing}
                  variant="outline"
                  onClick={() => {
                    store.addHeader({
                      id: headers.length,
                      value: "",
                      key: "",
                    });
                  }}
                >
                  Add Header
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={() => setIsOpen(false)}
            variant="outline"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
